import { Box, Chip, Paper, Stack, Typography, colors, useTheme } from "@mui/material";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import javascript from "highlight.js/lib/languages/javascript";
import json from "highlight.js/lib/languages/json";
import typescript from "highlight.js/lib/languages/typescript";
import yaml from "highlight.js/lib/languages/yaml";
import { useMemo, type ReactNode } from "react";

const highlighter = hljs.newInstance();
highlighter.registerLanguage("bash", bash);
highlighter.registerLanguage("javascript", javascript);
highlighter.registerLanguage("json", json);
highlighter.registerLanguage("typescript", typescript);
highlighter.registerLanguage("yaml", yaml);

export type CodeLanguage = "bash" | "javascript" | "json" | "typescript" | "yaml" | "plaintext";

export interface CodeBlockProps {
  children: string;
  language?: CodeLanguage;
  description?: string;
  actions?: ReactNode;
  "aria-label"?: string;
}

export const CodeBlock = ({ children, language = "plaintext", description, actions, "aria-label": ariaLabel = "Code block" }: CodeBlockProps) => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const highlighted = useMemo(
    () => (language === "plaintext" ? undefined : highlighter.highlight(children, { language, ignoreIllegals: true }).value),
    [children, language],
  );

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", bgcolor: dark ? colors.grey[900] : colors.grey[50] }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", minHeight: 32, px: 1.5, py: 0.25, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
          {description}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexShrink: 0 }}>
          <Chip size="small" variant="outlined" label={language === "plaintext" ? "Text" : language} />
          {actions}
        </Stack>
      </Stack>
      <Box
        component="pre"
        role="region"
        aria-label={ariaLabel}
        tabIndex={0}
        sx={{
          m: 0,
          p: 2,
          overflowX: "auto",
          fontSize: "0.8125rem",
          lineHeight: 1.65,
          fontFamily: 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace',
          color: "text.primary",
          "& code": { fontFamily: "inherit" },
          "& .hljs-keyword, & .hljs-selector-tag, & .hljs-literal": { color: dark ? colors.purple[200] : colors.purple[800] },
          "& .hljs-string, & .hljs-regexp, & .hljs-addition": { color: dark ? colors.green[200] : colors.green[800] },
          "& .hljs-number, & .hljs-symbol, & .hljs-bullet": { color: dark ? colors.orange[200] : colors.brown[800] },
          "& .hljs-attr, & .hljs-attribute, & .hljs-variable, & .hljs-template-variable": { color: dark ? colors.lightBlue[200] : colors.blue[800] },
          "& .hljs-title, & .hljs-built_in, & .hljs-type": { color: dark ? colors.amber[200] : colors.indigo[800] },
          "& .hljs-comment, & .hljs-quote": { color: "text.secondary", fontStyle: "italic" },
          "& .hljs-meta": { color: dark ? colors.cyan[200] : colors.teal[800] },
          "& .hljs-deletion": { color: dark ? colors.red[200] : colors.red[800] },
          "& .hljs-emphasis": { fontStyle: "italic" },
          "& .hljs-strong": { fontWeight: 700 },
        }}
      >
        {highlighted === undefined ? (
          <code>{children}</code>
        ) : (
          <code className={`hljs language-${language}`} dangerouslySetInnerHTML={{ __html: highlighted }} />
        )}
      </Box>
    </Paper>
  );
};
